import React, { useEffect, useState } from "react";
import { SaveEdit20Filled } from "@fluentui/react-icons";
import { Typography } from "@material-tailwind/react";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { fetchAllConfigs, updateConfig, ConfigItem } from "../../store/slices/configSlice";

interface SettingsTabProps {
  // Props are simplified as we now use Redux
}

const SettingsTab: React.FC<SettingsTabProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { configurations } = useSelector((state: RootState) => state.config);
  const [editingConfigName, setEditingConfigName] = useState<string | null>(null);
  const [editedValue, setEditedValue] = useState<string>("");
  
  useEffect(() => {
    // Fetch all configurations when component mounts
    dispatch(fetchAllConfigs());
  }, [dispatch]);
  
  const handleEditConfig = (config: ConfigItem) => {
    setEditingConfigName(config.name);
    setEditedValue(String(config.value));
  };
  
  const handleSaveConfig = (configName: string) => {
    // Parse the value based on the original config's value type
    const originalConfig = configurations.find(c => c.name === configName);
    
    if (originalConfig) {
      const valueType = originalConfig.value_type || typeof originalConfig.value;
      let parsedValue: string | number = editedValue;
      
      // Convert to number if original was a number
      if (valueType === 'number' || typeof originalConfig.value === 'number') {
        parsedValue = Number(editedValue);
        if (isNaN(parsedValue)) {
          alert('Please enter a valid number');
          return;
        }
      }
      
      dispatch(updateConfig({
        name: configName,
        value: parsedValue
      }));
      
      setEditingConfigName(null);
    }
  };
  
  const cancelEdit = () => {
    setEditingConfigName(null);
  };
  return (
    <div className="flex flex-col">
      <div className="p-2">
        <Typography
          variant="h2"
          className="font-inter font-normal text-base text-[#344054]"
          placeholder={undefined}
          onResize={undefined}
          onResizeCapture={undefined}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        >
          Change
        </Typography>
        <Typography
          variant="h3"
          className="font-inter font-bold text-2xl text-[#0C111D] mb-5"
          placeholder={undefined}
          onResize={undefined}
          onResizeCapture={undefined}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        >
          Services Controls
        </Typography>
      </div>
      {/* Settings table - similar styling to services table */}
      <div className="box-border bg-white border border-[#E4E7EC] shadow-xl rounded-lg overflow-x-auto">
        <table className="w-full min-w-max">
          <thead>
            <tr className="text-gray-600 text-sm bg-gray-50">
              <th className="text-left py-2 px-4 font-inter text-sm font-semibold text-[#475467] border-b border-[#E4E7EC] w-4/12">
                Configuration Name
              </th>
              <th className="text-left py-2 px-4 font-inter text-sm font-semibold text-[#475467] border-b border-[#E4E7EC] w-5/12">
                Refresh Time (in minutes)
              </th>
              <th className="text-left py-2 px-4 font-inter text-sm font-semibold text-[#475467] border-b border-[#E4E7EC] w-2/12"></th>
            </tr>
          </thead>
          <tbody>
            {configurations.map((config) => (
              <tr
                key={config.id || config.name}
                className="border-b border-[#E4E7EC] hover:bg-gray-50"
              >
                <td className="px-4 w-5/12">
                  <div className="flex items-center">
                    <div className="font-inter text-base text-[#344054]">
                      {config.name}
                    </div>
                  </div>
                </td>
                <td
                  className="px-4 font-inter text-base text-[#344054] w-5/12"
                  onClick={() => handleEditConfig(config)}
                >
                  <div className="h-8 flex items-center">
                    {editingConfigName === config.name ? (
                      <input
                        type={config.value_type === 'number' || typeof config.value === 'number' ? "number" : "text"}
                        value={editedValue}
                        onChange={(e) => setEditedValue(e.target.value)}
                        className="w-full border border-[#E4E7EC] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#6172F3] focus:border-[#6172F3]"
                        autoFocus
                        onBlur={(e) => {
                          // Only cancel if not clicking save button
                          const saveButton = document.getElementById(
                            `save-button-${config.name}`
                          );
                          if (!saveButton?.contains(e.relatedTarget as Node)) {
                            cancelEdit();
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveConfig(config.name);
                          } else if (e.key === 'Escape') {
                            cancelEdit();
                          }
                        }}
                      />
                    ) : (
                      <div className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded w-full">
                        {String(config.value)}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2 text-right w-2/12">
                  <div className="h-8 flex items-center justify-end">
                    {editingConfigName === config.name ? (
                      <button
                        id={`save-button-${config.name}`}
                        className="cursor-pointer w-5 border-none bg-transparent focus:border-none outline-none focus:outline-none focus:ring-0"
                        style={{ boxShadow: "none" }}
                        onClick={() => handleSaveConfig(config.name)}
                        tabIndex={0}
                      >
                        <SaveEdit20Filled className="w-5 h-5" />
                      </button>
                    ) : (
                      <SaveEdit20Filled 
                        className="w-5 h-5 opacity-30 hover:opacity-100 cursor-pointer" 
                        onClick={() => handleEditConfig(config)}
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} className="px-4 py-4"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default SettingsTab;
